import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Check,
  ChevronDown,
  Loader2,
  MessageSquare,
  Phone,
  Star,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "@/utils/api";
import { captureAttribution, getAttribution } from "@/utils/attribution";
import { getConsentLeadFields } from "@/utils/cookieConsent";
import { trackGenerateLead } from "@/utils/leadTracking";
import { AiDisclosureNotice } from "@/components/chatbot/AiDisclosureNotice";

// ─────────────────────────────────────────────────────────────────────────────
// Constants — real, verified data only (per Davids bekräftade uppgifter).
// ─────────────────────────────────────────────────────────────────────────────
const GOOGLE_REVIEWS_URL = "https://share.google/9RTbLJTbmmTSv7A83";
const STAR = "#FBBC04"; // Google-gul

const IMG = {
  hero: "/img/ai-agent-hemsida/hero.png",
  scenario: "/img/ai-agent-hemsida/scenario.png",
  laptop: "/img/ai-agent-hemsida/laptop.png",
  gransbygden: "/img/ai-agent-hemsida/gransbygden.png",
  bentech: "/img/ai-agent-hemsida/bentech.png",
};

// ─────────────────────────────────────────────────────────────────────────────
// Smooth-scroll helper — all primära test-CTA:n leder till samma formulär.
// ─────────────────────────────────────────────────────────────────────────────
function smoothScrollTo(hashHref) {
  if (!hashHref || !hashHref.startsWith("#")) return false;
  const el = document.getElementById(hashHref.slice(1));
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  if (window.history && window.history.replaceState) {
    window.history.replaceState(null, "", hashHref);
  }
  return true;
}

function ScrollCTA({ target = "#kontakt", label, variant = "primary", testId, className = "" }) {
  const base =
    variant === "primary"
      ? "bg-[#3A8A98] hover:bg-[#2c7a87] text-white shadow-lg shadow-[#3A8A98]/25"
      : "bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 hover:border-slate-300";
  return (
    <a
      href={target}
      onClick={(e) => { if (smoothScrollTo(target)) e.preventDefault(); }}
      className={`inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm transition-all duration-300 hover:translate-y-[-1px] ${base} ${className}`}
      data-testid={testId}
    >
      <span>{label}</span>
      <ArrowRight className="w-4 h-4" />
    </a>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Star rating — fem gula stjärnor
// ─────────────────────────────────────────────────────────────────────────────
function Stars({ size = "w-4 h-4", testId }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label="5 av 5 stjärnor" data-testid={testId}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className={size} style={{ fill: STAR, color: STAR }} aria-hidden="true" />
      ))}
    </span>
  );
}

function GoogleRating({ compact = false }) {
  return (
    <a
      href={GOOGLE_REVIEWS_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 ${compact ? "" : "px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm"} hover:opacity-90 transition-opacity`}
      data-testid="google-rating"
    >
      <Stars />
      <span className="text-[13px] font-semibold text-slate-800">5,0/5 på Google</span>
    </a>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hero — tydligt erbjudande, pris, testperiod, Google-betyg
// ─────────────────────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="relative pt-14 pb-16 lg:pt-20 lg:pb-24 overflow-hidden" data-testid="ai-chatbot-hero">
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse at 85% -10%, rgba(58,138,152,0.18) 0%, transparent 60%), radial-gradient(ellipse at 0% 100%, rgba(37,94,105,0.10) 0%, transparent 50%), linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-10 items-center">
          <div className="max-w-xl lg:col-span-3">
            <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase text-[#255E69] mb-6 px-3 py-1.5 rounded-full bg-[#3A8A98]/10 border border-[#3A8A98]/20">
              <MessageSquare className="w-3 h-3" />
              AI-chatbot för hemsidan
            </div>
            <h1
              className="font-['Barlow_Condensed'] text-[42px] sm:text-[52px] lg:text-[60px] font-bold text-[#0a1418] leading-[1.02] lg:leading-[0.98] tracking-[-0.02em] text-balance"
              data-testid="ai-chatbot-hero-title"
            >
              AI-chatbot som fångar fler{" "}
              <span className="bg-gradient-to-r from-[#255E69] via-[#3A8A98] to-[#5DBCCB] bg-clip-text text-transparent">
                förfrågningar
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Svara på kundernas frågor även när ni är upptagna. Elidos AI-chattbot
              hjälper besökaren vidare, samlar in relevanta uppgifter och lämnar över
              förfrågan till er.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <ScrollCTA target="#kontakt" label="Testa gratis i 14 dagar" variant="primary" testId="hero-cta-primary" />
              <ScrollCTA target="#exempel" label="Se hur det fungerar" variant="secondary" testId="hero-cta-secondary" />
            </div>

            {/* Erbjudande + betyg tydligt nära CTA */}
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="text-[15px] text-slate-700">
                <span className="font-semibold text-slate-900">14 dagar gratis</span>
                <span className="mx-2 text-slate-300">·</span>
                <span>därefter från <span className="font-semibold text-slate-900">990 kr/mån</span> exkl. moms</span>
              </div>
              <GoogleRating />
            </div>
            <p className="mt-3 text-[13px] text-slate-500">Elido hjälper er igång. Ingen bindningstid under testet.</p>
          </div>

          <div className="relative lg:col-span-2">
            <div
              aria-hidden="true"
              className="absolute -inset-6 bg-gradient-to-br from-[#3A8A98]/25 via-transparent to-[#8b5cf6]/10 rounded-[2rem] blur-3xl"
            />
            <div className="relative aspect-square rounded-3xl overflow-hidden shadow-2xl shadow-slate-900/20 border border-white/60 bg-slate-100">
              <img
                src={IMG.hero}
                alt="Företagare som tar emot en ny kundförfrågan via mobilen"
                className="w-full h-full object-cover"
                loading="eager"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Resultat + Google-omdömen (tidigt på sidan)
// ─────────────────────────────────────────────────────────────────────────────
function ResultsSection() {
  const stats = [
    { value: "Över 1 000", label: "konversationer hanterade" },
    { value: "Nästan var tredje", label: "konversation har lett till en kontaktförfrågan" },
    { value: "5,0/5", label: "på Google" },
  ];
  const reviews = [
    {
      name: "Samie Stenberg",
      text:
        "Elido levererar en extremt imponerande och smidig lösning för företag som vill automatisera sin leadskvalificering utan att förlora den personliga touchen. Deras AI-agenter (som Elido Nexus) är ljusår ifrån gamla tiders stela, regelbaserade chatbotar",
    },
    {
      name: "Jakob Jalmerot",
      text:
        "Duktig serviceinriktad kille som vi talat med.\nKom igång med våra tjänster efter någon enstaka minut.\nMkt nöjda!",
    },
  ];
  return (
    <section className="py-16 lg:py-20 bg-white" data-testid="ai-chatbot-results">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Resultat hittills
          </h2>
          <p className="mt-3 text-[15px] text-slate-500">
            Resultat från hittills hanterade konversationer. Utfallet varierar mellan företag.
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-5 mb-12">
          {stats.map((s, i) => (
            <div key={i} className="rounded-2xl border border-slate-100 bg-slate-50/60 p-6" data-testid={`result-stat-${i}`}>
              <div className="text-3xl font-bold text-[#255E69] tracking-tight">{s.value}</div>
              <div className="mt-1.5 text-[14px] text-slate-600 leading-snug">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {reviews.map((r, i) => (
            <figure key={i} className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 lg:p-7" data-testid={`review-card-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <Stars />
                <span className="text-[12px] font-semibold text-slate-400">Google</span>
              </div>
              <blockquote className="text-[15px] text-slate-700 leading-relaxed whitespace-pre-line">
                “{r.text}”
              </blockquote>
              <figcaption className="mt-4 text-[14px] font-semibold text-slate-900">{r.name}</figcaption>
            </figure>
          ))}
        </div>
        <div className="mt-6">
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="text-[14px] font-semibold text-[#255E69] hover:text-[#3A8A98] underline underline-offset-4" data-testid="all-reviews-link">
            Läs våra recensioner på Google →
          </a>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Illustrativt dialogexempel + vad företaget får
// ─────────────────────────────────────────────────────────────────────────────
function ExampleSection() {
  const messages = [
    { from: "user", text: "Hej! Kan ni hjälpa oss med en ny altan?" },
    { from: "bot", text: "Absolut. Ungefär hur stor altan gäller det, och på vilken ort?" },
    { from: "user", text: "Cirka 25 kvm i Markaryd." },
    { from: "bot", text: "Tack! Vill du helst bli kontaktad via telefon eller mejl, och när passar det?" },
    { from: "user", text: "Ring gärna på 070-123 45 67, gärna denna vecka." },
    { from: "bot", text: "Perfekt, jag sammanfattar och skickar vidare till teamet så hör de av sig." },
  ];
  return (
    <section id="exempel" className="py-20 lg:py-28 bg-gradient-to-b from-slate-50 to-white scroll-mt-20" data-testid="ai-chatbot-example">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase text-[#255E69] mb-5">
            <MessageSquare className="w-3 h-3" />
            Illustrativt exempel
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
            Från fråga till kundförfrågan
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Så här kan en dialog se ut. Exemplet är påhittat och visar hur boten
            hjälper besökaren och samlar in det ni behöver.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Chat */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-900/10 border border-slate-100 overflow-hidden" data-testid="example-chat">
            <div className="px-5 py-3.5 bg-gradient-to-r from-[#255E69] to-[#3A8A98] flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center border border-white/20">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              <div className="text-[13px] font-semibold text-white">Kundservice</div>
            </div>
            <AiDisclosureNotice />
            <div className="bg-slate-50/50 px-4 py-5 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[82%] px-4 py-2.5 text-[13px] leading-relaxed rounded-2xl ${m.from === "user" ? "bg-[#3A8A98] text-white rounded-br-md" : "bg-white text-slate-800 border border-slate-100 rounded-bl-md shadow-sm"}`}>
                    {m.text}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Underlaget företaget får */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 shadow-sm" data-testid="example-lead-summary">
            <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#255E69] mb-4">
              Detta får ni
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Sammanfattad förfrågan</h3>
            <ul className="space-y-3 text-[15px] text-slate-700">
              <li className="flex gap-3"><span className="font-semibold text-slate-500 w-28 shrink-0">Ärende</span><span>Bygga altan, ca 25 kvm</span></li>
              <li className="flex gap-3"><span className="font-semibold text-slate-500 w-28 shrink-0">Ort</span><span>Markaryd</span></li>
              <li className="flex gap-3"><span className="font-semibold text-slate-500 w-28 shrink-0">Kontaktväg</span><span>Telefon, 070-123 45 67</span></li>
              <li className="flex gap-3"><span className="font-semibold text-slate-500 w-28 shrink-0">Önskad tid</span><span>Denna vecka</span></li>
            </ul>
            <p className="mt-6 text-[13px] text-slate-500 leading-relaxed">
              Ni får en tydlig sammanfattning direkt — mer att gå på än ett vanligt
              kontaktformulär, och lättare att följa upp.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tre nyttor
// ─────────────────────────────────────────────────────────────────────────────
function BenefitsSection() {
  const benefits = [
    { icon: MessageSquare, title: "Svarar på vanliga frågor", text: "Boten svarar utifrån företagets egen information — tjänster, priser ni valt att dela, öppettider och rutiner." },
    { icon: Users, title: "Ställer relevanta följdfrågor", text: "Den hjälper besökaren att formulera sitt ärende genom korta, naturliga följdfrågor om behovet." },
    { icon: Phone, title: "Samlar kontaktuppgifter", text: "När besökaren är redo samlar boten in kontaktväg och ärende och lämnar över en tydlig förfrågan till er." },
  ];
  return (
    <section className="py-20 lg:py-24 bg-white" data-testid="ai-chatbot-benefits">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
            Mer än att bara svara på frågor
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <div key={i} className="p-7 rounded-2xl bg-slate-50/60 border border-slate-100" data-testid={`benefit-card-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[#3A8A98]/12 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#255E69]" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">{b.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{b.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Kundcase — Gränsbygdens Köpcentrum (bäst dokumenterade). Inga påhittade siffror.
// ─────────────────────────────────────────────────────────────────────────────
function CustomerCaseSection() {
  const cases = [
    {
      name: "Gränsbygdens Köpcentrum",
      role: "Köpcentrum · butiker & service",
      img: IMG.gransbygden,
      alt: "Gränsbygdens Köpcentrums entré",
      text:
        "På Gränsbygdens Köpcentrums hemsida hjälper AI-chattboten besökare med vanliga frågor, guidar dem till rätt information och fångar upp ärenden när personalen inte är tillgänglig. Det ger kunderna snabbare hjälp och minskar mängden återkommande frågor som personalen behöver hantera.",
      bullets: [
        "Svarar på vanliga kundfrågor",
        "Guidar besökare till rätt information",
        "Fångar upp fler förfrågningar utanför öppettider",
      ],
    },
    {
      name: "Bentech",
      role: "Öppningsbara glastak · sedan 1987",
      img: IMG.bentech,
      alt: "Bentechs lokal med skylten Bentech och www.bentech.se",
      text:
        "Bentech tillverkar skräddarsydda öppningsbara glastak. På deras hemsida svarar AI-chattboten på vanliga frågor om glastak och uterum, ställer relevanta följdfrågor om projektet och fångar upp förfrågningar så att teamet får ett tydligare underlag från start.",
      bullets: [
        "Besvarar frågor om glastak och uterum",
        "Ställer följdfrågor om projektet",
        "Lämnar vidare tydligare projektförfrågningar",
      ],
    },
  ];
  return (
    <section className="py-20 lg:py-28 bg-gradient-to-b from-slate-50 to-white" data-testid="ai-chatbot-case">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.14em] uppercase text-[#255E69] mb-4">
            <Users className="w-3 h-3" />
            Kundcase
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
            I drift hos riktiga företag
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {cases.map((c, i) => (
            <div key={i} className="rounded-3xl overflow-hidden border border-slate-100 bg-white shadow-sm" data-testid={`customer-case-${i}`}>
              <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                <img src={c.img} alt={c.alt} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-6 lg:p-8">
                <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#255E69] mb-2">{c.role}</div>
                <h3 className="text-2xl font-bold text-slate-900 leading-tight">{c.name}</h3>
                <p className="mt-4 text-[15px] text-slate-700 leading-relaxed border-l-2 border-[#3A8A98]/40 pl-4 italic">{c.text}</p>
                <ul className="mt-5 space-y-2.5">
                  {c.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-slate-700">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#3A8A98] shrink-0" />
                      <span className="text-[14px] leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-8 text-[14px]">
          <Link to="/digital-kundmottagare" className="font-semibold text-[#255E69] hover:text-[#3A8A98] underline underline-offset-4" data-testid="case-more-link">
            Läs mer om vår digitala kundmottagare →
          </Link>
        </p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tre steg för att komma igång
// ─────────────────────────────────────────────────────────────────────────────
function StepsSection() {
  const steps = [
    { num: "01", title: "Berätta om er verksamhet", text: "Lämna era uppgifter i formuläret. Vi hör av oss och sätter upp en AI-chattbot anpassad efter era tjänster och vanliga frågor." },
    { num: "02", title: "Vi sätter upp och ni godkänner", text: "Ni ser hur boten svarar och kan justera ton, frågor och vilka ärenden som ska skickas vidare innan den går live." },
    { num: "03", title: "Testa gratis i 14 dagar", text: "När boten är live på er hemsida börjar de 14 gratisdagarna. Därefter väljer ni själva om ni vill fortsätta." },
  ];
  return (
    <section id="sa-fungerar-det" className="py-20 lg:py-24 bg-white scroll-mt-20" data-testid="ai-chatbot-steps">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
            Så kommer ni igång
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">Tre enkla steg. Elido hjälper er hela vägen.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="relative bg-gradient-to-b from-white to-slate-50/50 rounded-2xl p-7 border border-slate-100" data-testid={`step-${i}`}>
              <span className="text-3xl font-bold text-slate-200 tracking-tight">{s.num}</span>
              <h3 className="mt-3 text-lg font-semibold text-slate-900 mb-2">{s.title}</h3>
              <p className="text-[15px] text-slate-600 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <ScrollCTA target="#kontakt" label="Testa gratis i 14 dagar" variant="primary" testId="steps-cta" />
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pris & testvillkor
// ─────────────────────────────────────────────────────────────────────────────
function PricingSection() {
  const included = [
    "AI-chattbot på er befintliga hemsida",
    "Anpassad efter era tjänster, ton och vanliga frågor",
    "Följdfrågor och insamling av kontaktuppgifter",
    "Förfrågningar sammanfattade och skickade till er",
    "Hjälp av Elido att komma igång",
  ];
  return (
    <section id="pris" className="py-20 lg:py-28 bg-gradient-to-b from-slate-50 to-white scroll-mt-20" data-testid="ai-chatbot-pricing">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
            Pris och testvillkor
          </h2>
        </div>
        <div className="rounded-3xl border border-slate-200 bg-white shadow-lg shadow-slate-900/5 p-8 lg:p-10">
          <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
            <span className="text-4xl font-bold text-slate-900">Från 990 kr</span>
            <span className="text-slate-500 pb-1">/mån exkl. moms</span>
          </div>
          <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">
            <span className="font-semibold text-slate-900">14 dagar gratis</span> när boten är live
            på er hemsida. Ingen bindning under testet. Efter testperioden väljer ni själva
            om ni vill fortsätta. Frånpriset gäller grundupplägget; omfattning och anpassning
            kan påverka priset.
          </p>
          <ul className="mt-7 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {included.map((it, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-700" data-testid={`pricing-item-${i}`}>
                <span className="mt-0.5 w-5 h-5 rounded-full bg-[#3A8A98]/15 border border-[#3A8A98]/30 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-[#255E69]" />
                </span>
                <span className="text-[15px] leading-relaxed">{it}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <ScrollCTA target="#kontakt" label="Testa gratis i 14 dagar" variant="primary" testId="pricing-cta" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FAQ
// ─────────────────────────────────────────────────────────────────────────────
const FAQS = [
  {
    q: "Vad kostar tjänsten?",
    a: "Från 990 kr/mån exkl. moms för grundupplägget. Omfattning och anpassning kan påverka priset. De första 14 dagarna är gratis.",
  },
  {
    q: "Vad ingår i testet och vad händer efteråt?",
    a: "Ni får en AI-chattbot uppsatt på er hemsida och testar den i 14 dagar utan bindning. När testet är slut väljer ni själva om ni vill fortsätta på ett månadsupplägg — annars avslutas det.",
  },
  {
    q: "Behöver vi byta hemsida?",
    a: "Nej. Boten läggs på er befintliga hemsida. Ni behöver inte byta plattform eller bygga om sidan.",
  },
  {
    q: "Hur kommer vi igång?",
    a: "Lämna era uppgifter i formuläret. Vi hör av oss, sätter upp boten efter er verksamhet och låter er godkänna innan den går live.",
  },
  {
    q: "Hur får vi kundernas förfrågningar?",
    a: "När en besökare lämnar kontaktuppgifter skickas en sammanfattning av ärendet till er via mejl, med kontaktväg och relevant information från konversationen.",
  },
  {
    q: "Vad händer om chatbotten inte vet svaret?",
    a: "Den lovar inte saker den inte kan svara på. När företagets egen bedömning behövs guidar den istället besökaren till att lämna en förfrågan så att ni kan ta över.",
  },
  {
    q: "Hur hanteras personuppgifter?",
    a: "Uppgifterna som besökaren lämnar används för att hantera förfrågan och kontakta er. Vi hjälper er sätta upp lösningen efter era rutiner. Läs mer i vår integritetspolicy.",
  },
];

function FAQItem({ q, a, idx, open, onToggle }) {
  return (
    <div className="border-b border-slate-200 last:border-0" data-testid={`faq-item-${idx}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 py-5 text-left group"
        data-testid={`faq-toggle-${idx}`}
        aria-expanded={open}
      >
        <span className="text-base lg:text-lg font-semibold text-slate-900 group-hover:text-[#255E69] transition-colors">{q}</span>
        <span className={`shrink-0 w-8 h-8 rounded-full bg-slate-100 group-hover:bg-[#3A8A98]/15 flex items-center justify-center transition-all ${open ? "rotate-180" : ""}`}>
          <ChevronDown className="w-4 h-4 text-slate-700" />
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-[400px] pb-5" : "max-h-0"}`}>
        <p className="text-[15px] text-slate-600 leading-relaxed pr-12">{a}</p>
      </div>
    </div>
  );
}

function FAQSection() {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section className="py-20 lg:py-24 bg-white" data-testid="ai-chatbot-faq">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight">Vanliga frågor</h2>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 px-6 lg:px-8 shadow-sm">
          {FAQS.map((f, i) => (
            <FAQItem key={i} q={f.q} a={f.a} idx={i} open={openIdx === i} onToggle={() => setOpenIdx(openIdx === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Formulär — återanvänder /api/contact (syns i /admin/leads som contact_form)
// ─────────────────────────────────────────────────────────────────────────────
function FormField({ label, value, onChange, placeholder, type = "text", required, testId, error }) {
  const cls =
    "w-full px-4 py-3 rounded-xl bg-white border text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:ring-2 transition-colors " +
    (error ? "border-red-400 focus:border-red-500 focus:ring-red-500/20" : "border-slate-200 focus:border-[#3A8A98] focus:ring-[#3A8A98]/20");
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-2 uppercase tracking-wide">
        {label} {required ? <span className="text-[#3A8A98]">*</span> : null}
      </label>
      {type === "textarea" ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={4} className={cls + " resize-none"} data-testid={testId} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cls} data-testid={testId} />
      )}
      {error ? <p className="mt-1.5 text-[12px] text-red-500" data-testid={`${testId}-error`}>{error}</p> : null}
    </div>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", website: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { captureAttribution(); }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Fyll i ditt namn";
    if (!form.email.trim()) e.email = "Fyll i din e-post";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Ange en giltig e-postadress";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || submitted) return; // skydd mot dubbelklick / återförsök
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        company: form.company.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        website: form.website.trim(),
        message:
          (form.message || "").trim() ||
          "Vill testa AI-chattbot på hemsidan (från /ai-chatbot-hemsida)",
        attribution: getAttribution(),
        ...getConsentLeadFields(),
      };
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!res.ok || result?.success === false) {
        throw new Error(result?.error || "Kunde inte skicka");
      }
      trackGenerateLead({
        accepted: result?.accepted,
        lead_id: result?.lead_id,
        form_source: "ai_chatbot_hemsida_contact",
      });
      setSubmitted(true);
      toast.success("Tack! Vi hör av oss om uppsättningen.");
      setForm({ name: "", company: "", email: "", phone: "", website: "", message: "" });
    } catch (err) {
      toast.error(err.message || "Något gick fel. Försök igen.");
      setSubmitting(false);
    }
  };

  return (
    <section id="kontakt" className="py-20 lg:py-28 bg-gradient-to-b from-slate-50 to-white scroll-mt-20" data-testid="ai-chatbot-contact">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4"><GoogleRating /></div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
            Testa en AI-chattbot på er hemsida
          </h2>
          <p className="mt-4 text-lg text-slate-600 leading-relaxed">
            Lämna era uppgifter så kontaktar vi er om uppsättningen. 14 dagar gratis,
            därefter från 990 kr/mån exkl. moms.
          </p>
        </div>

        {submitted ? (
          <div className="bg-gradient-to-br from-[#3A8A98]/8 to-[#8b5cf6]/5 border border-[#3A8A98]/20 rounded-2xl p-10 text-center" data-testid="contact-success">
            <div className="w-14 h-14 rounded-full bg-[#3A8A98]/15 border border-[#3A8A98]/30 flex items-center justify-center mx-auto mb-5">
              <Check className="w-7 h-7 text-[#255E69]" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Tack!</h3>
            <p className="text-slate-600">Vi hör av oss om uppsättningen.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-10 shadow-lg shadow-slate-900/5 space-y-5" data-testid="contact-form" noValidate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField label="Namn" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Ditt namn" required testId="contact-name" error={errors.name} />
              <FormField label="Företag" value={form.company} onChange={(v) => setForm({ ...form, company: v })} placeholder="(valfritt)" testId="contact-company" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField label="E-post" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="namn@foretag.se" required testId="contact-email" error={errors.email} />
              <FormField label="Telefon" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="(valfritt)" testId="contact-phone" />
            </div>
            <FormField label="Er hemsida" type="url" value={form.website} onChange={(v) => setForm({ ...form, website: v })} placeholder="https://ertforetag.se (valfritt)" testId="contact-website" />
            <FormField label="Berätta kort om er verksamhet" type="textarea" value={form.message} onChange={(v) => setForm({ ...form, message: v })} placeholder="Vad gör ni, och vilka frågor brukar era kunder ställa? (valfritt)" testId="contact-message" />
            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-[#3A8A98] hover:bg-[#2c7a87] text-white font-semibold text-sm transition-all duration-300 hover:translate-y-[-1px] shadow-lg shadow-[#3A8A98]/25 disabled:opacity-60"
              data-testid="contact-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              Jag vill testa gratis
            </button>
            <p className="text-[12px] text-slate-500 text-center pt-2">
              Genom att skicka godkänner du att vi kontaktar dig om uppsättningen. Inga förpliktelser.
            </p>
          </form>
        )}

        {/* Diskret självservice-alternativ */}
        <p className="mt-8 text-center text-[14px] text-slate-500">
          Vill du hellre bygga själv?{" "}
          <Link to="/skapa-ai-agent" className="font-semibold text-[#255E69] hover:text-[#3A8A98] underline underline-offset-4" data-testid="self-service-link">
            Skapa ett utkast direkt i webbläsaren
          </Link>
        </p>

        {/* Interna länkar till relevanta sidor */}
        <nav className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[13px] text-slate-500" aria-label="Relaterat" data-testid="related-links">
          <Link to="/digital-kundmottagare" className="hover:text-[#255E69] underline underline-offset-4">Digital kundmottagare</Link>
          <Link to="/chatbot-ehandel" className="hover:text-[#255E69] underline underline-offset-4">AI-chatbot för e-handel</Link>
          <Link to="/hemsidor" className="hover:text-[#255E69] underline underline-offset-4">Ny hemsida</Link>
          <Link to="/blog" className="hover:text-[#255E69] underline underline-offset-4">Blogg</Link>
        </nav>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page wrapper
// ─────────────────────────────────────────────────────────────────────────────
export default function AiChatbotHemsidaPage() {
  return (
    <>
      <Helmet>
        <title>AI-chattbot för hemsidan – från 990 kr/mån | Elido</title>
        <meta
          name="description"
          content="Testa en AI-chattbot på er hemsida gratis i 14 dagar. Svara på kundfrågor och fånga kontaktförfrågningar. Därefter från 990 kr/mån exkl. moms."
        />
        <meta property="og:title" content="AI-chattbot för hemsidan – från 990 kr/mån | Elido" />
        <meta
          property="og:description"
          content="Testa en AI-chattbot på er hemsida gratis i 14 dagar. Svara på kundfrågor och fånga kontaktförfrågningar. Därefter från 990 kr/mån exkl. moms."
        />
        <meta property="og:url" content="https://elido.nu/ai-chatbot-hemsida" />
        <link rel="canonical" href="https://elido.nu/ai-chatbot-hemsida" />
      </Helmet>

      <main className="bg-white text-slate-900" data-testid="ai-chatbot-page">
        <Hero />
        <ResultsSection />
        <ExampleSection />
        <BenefitsSection />
        <CustomerCaseSection />
        <StepsSection />
        <PricingSection />
        <FAQSection />
        <ContactSection />
      </main>
    </>
  );
}
